import React, { useCallback, useState, useEffect, useMemo } from 'react'
import { useHistory } from 'react-router-dom'

import classnames from 'classnames'

import {
  useDelivery,
  RangedCustomerAddress
} from 'src/public/components/online_ordering/DeliveryContext'
import {
  CartDeliveryInfo,
  useFulfillment
} from 'src/public/components/online_ordering/FulfillmentContext'
import { useCart } from 'src/public/components/online_ordering/CartContext'
import { useRestaurantRoutes } from 'src/shared/components/common/restaurant_routes/RestaurantRoutesContext'
import { Channel } from 'src/public/js/siteUtilities'

import {
  Modal,
  ModalContent,
  ModalOverlay
} from 'src/shared/components/common/modal/Modal'

import { filterCustomerAddresses } from './deliveryAddressUtils'

import CheckoutRequirementScreen from './CheckoutRequirementScreen'
import AddressHubScreen from './AddressHubScreen'
import AddressSearchScreen from './AddressSearchScreen'
import AddressInvalidScreen from './AddressInvalidScreen'
import AddressConfirmScreen from './AddressConfirmScreen'
import { TrackingSource } from 'src/public/components/default_template/online_ordering/dining_behavior_toggle/fulfillmentTrackingUtils'
import ManualAddressScreen from 'src/public/components/default_template/online_ordering/delivery_address_modal/ManualAddressScreen'
import { DeliveryInfoInput } from 'src/apollo/onlineOrdering'
import { isAddressMatch } from 'src/public/components/online_ordering/addressUtils'

/**
 * Custom hook for navigating to checkout after completing delivery modal actions.
 */
export const useNavigateToCheckout = () => {
  const { channel } = useCart()
  const { checkoutPath, ecommCheckoutPath } = useRestaurantRoutes()
  const history = useHistory()

  const navigateToCheckout = useCallback(() => {
    const targetCheckoutPath =
      channel === Channel.ECOMM && ecommCheckoutPath
        ? ecommCheckoutPath
        : checkoutPath
    history.push(targetCheckoutPath)
  }, [channel, ecommCheckoutPath, checkoutPath, history])

  return navigateToCheckout
}

interface DeliveryModalProps {
  isOpen: boolean
  onClose: () => void
  forceScreenPlacement?: DeliveryAddressScreen
  forceTrackingSource?: TrackingSource
  returnFocusElementId?: string
}

export type DeliveryAddressScreen =
  | 'checkout-requirement'
  | 'address-hub'
  | 'address-search'
  | 'address-invalid'
  | 'address-confirm'
  | 'manual-address'

export interface ScreenProps {
  goToScreen: (
    screen: DeliveryAddressScreen,
    deliveryAddress?: CartDeliveryInfo | null,
    source?: TrackingSource,
    partialAddress?: Partial<DeliveryInfoInput> | null,
    searchText?: string | null
  ) => void
  closeModal: () => void
  deliveryAddress: CartDeliveryInfo | null
  inRangeAddresses: RangedCustomerAddress[]
  outOfRangeAddresses: RangedCustomerAddress[]
  trackingSource: TrackingSource
  shouldNavigateToCheckoutOnComplete: boolean
  allowBackNav: boolean
  partialDeliveryAddress: Partial<DeliveryInfoInput> | null
  previousSearchText: string | null
}

/**
 * This flow is intentionally INDEPENDENT of most DeliveryContext values
 * in hopes that it will win in an A/B test and the DeliveryContext
 * can be sufficiently decoupled and trimmed down.
 *
 * TODO: Will need to accept call-site identifiers for tracking event specificity.
 */
const DeliveryModal = ({
  isOpen,
  onClose,
  forceScreenPlacement,
  forceTrackingSource,
  returnFocusElementId
}: DeliveryModalProps) => {
  // pull pre-validated saved addresses for screen eligibility
  const { customerAddresses } = useDelivery()
  const { fulfillmentData } = useFulfillment()
  const [deliveryAddress, setDeliveryAddress] =
    useState<CartDeliveryInfo | null>(null)
  const [currentScreen, setCurrentScreen] = useState<DeliveryAddressScreen>(
    forceScreenPlacement ?? 'address-search'
  )
  // Track partial address separately from confirmed deliveryAddress
  const [partialDeliveryAddress, setPartialDeliveryAddress] =
    useState<Partial<DeliveryInfoInput> | null>(null)
  // Track previous search text for "no results" state preservation
  const [previousSearchText, setPreviousSearchText] = useState<string | null>(
    null
  )

  // Pre-process all address data at modal level
  const sessionDeliveryInfo = useMemo(
    () => fulfillmentData?.cartFulfillmentData?.deliveryInfo ?? null,
    [fulfillmentData?.cartFulfillmentData?.deliveryInfo]
  )

  // Helper function to enrich address with saved address data
  const enrichAddressWithSavedData = useCallback(
    (address: CartDeliveryInfo | null): CartDeliveryInfo | null => {
      if (!address) return null

      const matchingCustomerAddress = customerAddresses.find((addr) =>
        isAddressMatch(addr, address)
      )

      if (matchingCustomerAddress) {
        return {
          ...address,
          name: matchingCustomerAddress.name,
          guid: matchingCustomerAddress.guid
        }
      }

      return address
    },
    [customerAddresses]
  )

  const [inRangeAddresses, outOfRangeAddresses] = useMemo(
    () => filterCustomerAddresses(customerAddresses, sessionDeliveryInfo),
    [customerAddresses, sessionDeliveryInfo]
  )

  useEffect(() => {
    const initialScreen =
      customerAddresses.length > 0 || sessionDeliveryInfo
        ? 'address-hub'
        : 'address-search'
    if (isOpen) {
      setCurrentScreen(forceScreenPlacement ?? initialScreen)
      setDeliveryAddress(enrichAddressWithSavedData(sessionDeliveryInfo))
    } else {
      setCurrentScreen(initialScreen)
    }
  }, [
    isOpen,
    forceScreenPlacement,
    customerAddresses.length,
    sessionDeliveryInfo,
    enrichAddressWithSavedData
  ])

  /**
   * Note it is a screen's responsibility to stage the next's screens state of the
   * tentative deliveryAddress being configured in this modal. That may require wiping it.
   * A screen should never set an invalid address when transitioning to the address confirmation
   * screen.
   */
  const handleScreenNavigation = useCallback<ScreenProps['goToScreen']>(
    (
      screen: DeliveryAddressScreen,
      deliveryAddress,
      source,
      partialAddress,
      searchText
    ) => {
      if (source) {
        setTrackingSource(source)
      }
      // Update all state in a single setState call to ensure atomic updates and prevent flicker
      setCurrentScreen(screen)
      if (deliveryAddress === null) {
        setDeliveryAddress(null)
      } else if (!!deliveryAddress) {
        setDeliveryAddress(enrichAddressWithSavedData(deliveryAddress))
      }

      // Update partialDeliveryAddress to pre-populate manual address fields with it
      if (partialAddress) {
        setPartialDeliveryAddress(partialAddress)
      }

      // Update previousSearchText for "no results" state preservation
      if (searchText !== undefined) {
        setPreviousSearchText(searchText)
      }
    },
    [enrichAddressWithSavedData]
  )

  const [trackingSource, setTrackingSource] = useState<TrackingSource>(
    forceTrackingSource || TrackingSource.Checkout
  )

  const renderCurrentScreen = () => {
    const screenProps: ScreenProps = {
      goToScreen: handleScreenNavigation,
      closeModal: onClose,
      deliveryAddress,
      inRangeAddresses,
      outOfRangeAddresses,
      trackingSource,
      shouldNavigateToCheckoutOnComplete:
        forceScreenPlacement === 'checkout-requirement',
      allowBackNav: forceScreenPlacement !== 'address-confirm',
      partialDeliveryAddress,
      previousSearchText
    }

    switch (currentScreen) {
      case 'checkout-requirement':
        return <CheckoutRequirementScreen {...screenProps} />
      case 'address-hub':
        return <AddressHubScreen {...screenProps} />
      case 'address-search':
        return <AddressSearchScreen {...screenProps} />
      case 'address-invalid':
        return <AddressInvalidScreen {...screenProps} />
      case 'address-confirm':
        return <AddressConfirmScreen {...screenProps} />
      case 'manual-address':
        return <ManualAddressScreen {...screenProps} />
      default:
        return <AddressSearchScreen {...screenProps} />
    }
  }

  const useBottomDrawer = useMemo(
    () => ['checkout-requirement', 'address-invalid'].includes(currentScreen),
    [currentScreen]
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      testId='delivery-address-modal'
      preventOverlayClose
    >
      <ModalOverlay />
      <ModalContent
        contentClassName={classnames(
          'modalContentV2 fulfillmentModal deliveryAddressModal',
          useBottomDrawer ? 'bottomDrawerMobile' : 'fullScreenMobile'
        )}
        ariaLabelledBy='delivery-address-modal-title'
        returnFocusElementId={returnFocusElementId}
      >
        {renderCurrentScreen()}
      </ModalContent>
    </Modal>
  )
}

export default DeliveryModal
